import { complianceOverviewListQuerySchema, type ComplianceOverviewItem, type ComplianceOverviewListQuery, type Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { toListParams } from '../../../shared/policies/list-params';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { listComplianceOverview } from '../compliance.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type ComplianceOverviewState =
  { status: 'loading' } | { status: 'loaded'; page: Page<ComplianceOverviewItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: ComplianceOverviewListQuery) => toListParams(query).toString();

function toState(page: Page<ComplianceOverviewItem>, query: ComplianceOverviewListQuery): ComplianceOverviewState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): ComplianceOverviewState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of compliance exceptions the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. */
export function useComplianceOverview() {
  const list = useListQuery(complianceOverviewListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<ComplianceOverviewState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listComplianceOverview(query)
      .then((page) => {
        if (!cancelled) setState(toState(page, query));
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  return { state, list, isRefreshing };
}
